import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Product } from "../../../types/product";
import { useCart } from "../../../context/CartContext";
import { useCompare } from "@/context/CompareContext";
import { useWishlist } from "@/context/WishlistContext";
import QuickViewModal from "./QuickViewModal";

interface Props {
  product: Product;
  viewMode?: "grid" | "list";
}

interface BadgeDef {
  label: string;
  icon: string;
  className: string;
}

function buildBadges(product: Product): BadgeDef[] {
  const badges: BadgeDef[] = [];
  if (!product.inStock) return badges;
  if (product.badge === "Best Seller") {
    badges.push({ label: "Best Seller", icon: "ri-trophy-line", className: "bg-amber-500 text-white" });
  }
  if (product.isNew) {
    badges.push({ label: "New Arrival", icon: "ri-sparkling-2-line", className: "bg-emerald-500 text-white" });
  }
  if (product.isLimitedStock) {
    badges.push({ label: "Limited Stock", icon: "ri-alarm-warning-line", className: "bg-orange-500 text-white" });
  }
  return badges;
}

function StarRating({ rating, reviews }: { rating: number; reviews: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-px">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`text-xs ${
              star <= Math.floor(rating)
                ? "ri-star-fill text-amber-400"
                : star - rating < 1
                ? "ri-star-half-fill text-amber-400"
                : "ri-star-line text-gray-300"
            }`}
          ></i>
        ))}
      </div>
      <span className="text-xs text-gray-400">({reviews.toLocaleString()})</span>
    </div>
  );
}

function BadgeStack({ badges }: { badges: BadgeDef[] }) {
  if (!badges.length) return null;
  return (
    <div className="absolute top-2.5 right-2.5 flex flex-col gap-1 items-end z-10">
      {badges.map((b) => (
        <span
          key={b.label}
          className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap ${b.className}`}
        >
          <i className={`${b.icon} text-[9px]`}></i>
          {b.label}
        </span>
      ))}
    </div>
  );
}

export default function ProductCard({ product, viewMode = "grid" }: Props) {
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);
  const [heartPop, setHeartPop] = useState(false);
  const [quickView, setQuickView] = useState(false);
  const { addItem } = useCart();
  const { isInCompare, toggleCompare, canAdd } = useCompare();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const inCompare = isInCompare(product.id);
  const compareDisabled = !inCompare && !canAdd;
  const wishlisted = isWishlisted(product.id);
  const badges = buildBadges(product);

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleWishlist({
      id: product.id,
      variantId: product.variantId,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      inStock: product.inStock,
      category: product.category,
    });
    if (!wishlisted) {
      setHeartPop(true);
      setTimeout(() => setHeartPop(false), 600);
    }
  };

  const saveDollars = product.originalPrice ? product.originalPrice - product.price : null;
  const savePct = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product.inStock) return;
    addItem({
      id: product.id,
      variantId: product.variantId,
      name: product.name,
      brand: product.brand,
      price: product.price,
      image: product.image,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (viewMode === "list") {
    return (
      <>
      <div
        className="bg-white border border-gray-100 rounded-lg overflow-hidden flex hover:border-amber-300 hover:shadow-sm transition-all duration-200 cursor-pointer group"
        onClick={() => navigate(`/shop/${product.id}`)}
      >
        {/* Image */}
        <div className="relative w-48 shrink-0 bg-gray-50">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
          />
          {saveDollars && product.inStock && (
            <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">
              Save ${saveDollars.toLocaleString()}
            </span>
          )}
          {!product.inStock && (
            <span className="absolute top-2 left-2 bg-gray-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">
              Sold Out
            </span>
          )}
          <BadgeStack badges={badges} />
          {/* Wishlist heart */}
          <button
            onClick={handleToggleWishlist}
            title={wishlisted ? "Remove from wishlist" : "Save to wishlist"}
            className={`absolute bottom-2 right-2 w-7 h-7 flex items-center justify-center rounded-full border transition-all cursor-pointer z-10 ${
              wishlisted
                ? "bg-rose-500 border-rose-500 text-white shadow-sm"
                : "bg-white/90 border-gray-200 text-gray-400 hover:text-rose-500 hover:border-rose-300"
            } ${heartPop ? "scale-125" : "scale-100"}`}
            style={{ transition: "transform 0.2s ease, background-color 0.15s, color 0.15s" }}
          >
            <i className={wishlisted ? "ri-heart-fill text-xs" : "ri-heart-line text-xs"}></i>
          </button>
        </div>

        {/* Info */}
        <div className="flex flex-col justify-between p-5 flex-1">
          <div>
            <p className="text-xs text-gray-400 font-medium mb-0.5 uppercase tracking-wide">{product.brand}</p>
            <h3 className="font-semibold text-gray-900 text-sm mb-2 leading-snug">{product.name}</h3>
            <StarRating rating={product.rating} reviews={product.reviews} />
            <div className="flex items-center gap-1.5 mt-1 text-xs text-emerald-600 font-medium">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-truck-line"></i>
              </div>
              Free Shipping
            </div>
            <Link
              to="/warranty"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 mt-1 text-xs text-amber-600 font-medium hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-shield-check-line"></i>
              </div>
              Mfr. Warranty Included
            </Link>
            {product.isLimitedStock && product.inStock && (
              <div className="flex items-center gap-1 mt-1 text-xs text-orange-600 font-semibold">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-alarm-warning-line"></i>
                </div>
                Only a few left — order soon
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-3">
            <div>
              {product.originalPrice ? (
                <>
                  <span className="font-extrabold text-red-600 text-xl">${product.price.toLocaleString()}</span>
                  <span className="text-sm text-gray-400 line-through ml-2">${product.originalPrice.toLocaleString()}</span>
                  {savePct && <span className="ml-2 text-xs text-red-500 font-semibold">({savePct}% off)</span>}
                </>
              ) : (
                <span className="font-extrabold text-gray-900 text-xl">${product.price.toLocaleString()}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); setQuickView(true); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600 hover:bg-amber-50 transition-all whitespace-nowrap cursor-pointer bg-white"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </div>
                Quick View
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); toggleCompare(product); }}
                disabled={compareDisabled}
                title={compareDisabled ? "Max 3 products can be compared" : inCompare ? "Remove from comparison" : "Add to comparison"}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-all whitespace-nowrap cursor-pointer ${
                  inCompare
                    ? "bg-amber-50 border-amber-400 text-amber-700"
                    : compareDisabled
                    ? "bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed"
                    : "bg-white border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600"
                }`}
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={inCompare ? "ri-checkbox-fill text-amber-500" : "ri-checkbox-blank-line"}></i>
                </div>
                Compare
              </button>
              <button
                onClick={handleAddToCart}
                disabled={!product.inStock}
                className={`px-5 py-2 rounded-md text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                  added
                    ? "bg-emerald-500 text-white"
                    : product.inStock
                    ? "bg-amber-500 hover:bg-amber-400 text-white"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {added ? "Added!" : product.inStock ? "Add to Cart" : "Out of Stock"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <QuickViewModal product={quickView ? product : null} onClose={() => setQuickView(false)} />
      </>
    );
  }

  return (
    <>
    <div
      className="bg-white border border-gray-100 rounded-lg overflow-hidden group hover:border-amber-300 hover:shadow-sm transition-all duration-200 flex flex-col cursor-pointer"
      onClick={() => navigate(`/shop/${product.id}`)}
    >
      {/* Image */}
      <div className="relative w-full h-52 bg-gray-50 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain p-5 group-hover:scale-105 transition-transform duration-300"
        />
        {/* Sale badge — top left */}
        {saveDollars && product.inStock && (
          <span className="absolute top-2.5 left-2.5 bg-red-500 text-white text-[10px] font-extrabold px-2.5 py-1 rounded z-10">
            Save ${saveDollars.toLocaleString()}
          </span>
        )}
        {/* Sold out overlay */}
        {!product.inStock && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
            <span className="bg-gray-700 text-white text-xs font-bold px-3 py-1.5 rounded">Sold Out</span>
          </div>
        )}
        {/* Stacked status badges — top right */}
        <BadgeStack badges={badges} />
        {/* Warranty badge — bottom left */}
        {!saveDollars && product.inStock && (
          <Link
            to="/warranty"
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-2.5 left-2.5 flex items-center gap-1 bg-white/90 border border-amber-200 text-amber-600 text-[10px] font-bold px-2 py-1 rounded-md hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap z-10"
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-shield-check-line text-xs"></i>
            </div>
            Warranty Included
          </Link>
        )}
        {/* Wishlist heart — bottom right */}
        <button
          onClick={handleToggleWishlist}
          title={wishlisted ? "Remove from wishlist" : "Save to wishlist"}
          className={`absolute bottom-2.5 right-2.5 w-7 h-7 flex items-center justify-center rounded-full border transition-all cursor-pointer z-10 ${
            wishlisted
              ? "bg-rose-500 border-rose-500 text-white shadow-sm"
              : "bg-white/90 border-gray-200 text-gray-400 hover:text-rose-500 hover:border-rose-300"
          } ${heartPop ? "scale-125" : "scale-100"}`}
          style={{ transition: "transform 0.2s ease, background-color 0.15s, color 0.15s" }}
        >
          <i className={wishlisted ? "ri-heart-fill text-xs" : "ri-heart-line text-xs"}></i>
        </button>

        {/* Quick View — appears on hover, centered bottom of image */}
        <button
          onClick={(e) => { e.stopPropagation(); setQuickView(true); }}
          className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-gray-900/80 hover:bg-gray-900 text-white text-[10px] font-bold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 cursor-pointer whitespace-nowrap z-10 backdrop-blur-sm"
        >
          <i className="ri-eye-line text-xs"></i>
          Quick View
        </button>
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-widest mb-0.5">{product.brand}</p>
        <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-2 flex-1 line-clamp-2">{product.name}</h3>

        <StarRating rating={product.rating} reviews={product.reviews} />

        {/* Price */}
        <div className="mt-2 mb-1">
          {product.originalPrice ? (
            <div>
              <div className="flex items-baseline gap-2">
                <span className="font-extrabold text-red-600 text-lg">${product.price.toLocaleString()}</span>
                <span className="text-sm text-gray-400 line-through">${product.originalPrice.toLocaleString()}</span>
              </div>
              {savePct && (
                <p className="text-xs text-red-500 font-semibold mt-0.5">Save {savePct}% (${saveDollars?.toLocaleString()})</p>
              )}
            </div>
          ) : (
            <span className="font-extrabold text-gray-900 text-lg">${product.price.toLocaleString()}</span>
          )}
        </div>

        {/* Free shipping + warranty row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-truck-line"></i>
            </div>
            Free Shipping
          </div>
          <Link
            to="/warranty"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 text-xs text-amber-600 font-medium hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-shield-check-line"></i>
            </div>
            Mfr. Warranty
          </Link>
        </div>

        {/* Limited stock nudge */}
        {product.isLimitedStock && product.inStock && (
          <div className="flex items-center gap-1 text-xs text-orange-600 font-semibold mb-2">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-alarm-warning-line"></i>
            </div>
            Only a few left — order soon
          </div>
        )}

        {/* Add to Cart */}
        <button
          onClick={handleAddToCart}
          disabled={!product.inStock}
          className={`w-full py-2.5 rounded-md text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
            added
              ? "bg-emerald-500 text-white"
              : product.inStock
              ? "bg-amber-500 hover:bg-amber-400 text-white"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {added ? (
            <span className="flex items-center justify-center gap-1.5">
              <i className="ri-check-line"></i> Added to Cart
            </span>
          ) : product.inStock ? (
            "Add to Cart"
          ) : (
            "Out of Stock"
          )}
        </button>

        {/* Compare toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); toggleCompare(product); }}
          disabled={compareDisabled}
          title={compareDisabled ? "Max 3 products can be compared" : inCompare ? "Remove from comparison" : "Add to comparison"}
          className={`mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold border transition-all whitespace-nowrap cursor-pointer ${
            inCompare
              ? "bg-amber-50 border-amber-300 text-amber-700"
              : compareDisabled
              ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed"
              : "bg-white border-gray-200 text-gray-400 hover:border-amber-300 hover:text-amber-600 hover:bg-amber-50/50"
          }`}
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={inCompare ? "ri-checkbox-fill text-amber-500" : "ri-checkbox-blank-line"}></i>
          </div>
          {inCompare ? "Added to Compare" : "Compare"}
        </button>
      </div>
    </div>
    <QuickViewModal product={quickView ? product : null} onClose={() => setQuickView(false)} />
    </>
  );
}
